import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Package, Truck, User, LogOut, ShoppingCart, MapPin, Search, ChevronRight, Menu, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { SamaanDenaLogo } from './Logo';
import { LanguageSelector, LanguageSelectorMini } from './LanguageSelector';

export const Navbar = () => {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getDashboardLink = () => {
    if (!user) return '/';
    switch (user.role) {
      case 'customer':
        return '/customer/dashboard';
      case 'shop_owner':
        return '/shop/dashboard';
      case 'delivery_agent':
        return '/delivery/dashboard';
      default:
        return '/';
    }
  };

  const getRoleLabel = () => {
    if (!user) return '';
    switch (user.role) {
      case 'customer':
        return t('customer');
      case 'shop_owner':
        return t('shopOwner');
      case 'delivery_agent':
        return t('deliveryAgent');
      default:
        return '';
    }
  };

  return (
    <nav 
      className={`sticky top-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? "bg-white/95 backdrop-blur-xl border-b border-gray-100 shadow-sm" 
          : "bg-white"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo">
            <SamaanDenaLogo className="w-10 h-10 transition-transform group-hover:scale-110" />
            <div className="flex flex-col">
              <span className="font-bold text-xl md:text-2xl bg-gradient-to-r from-emerald-600 to-orange-500 bg-clip-text text-transparent">
                SamaanDena
              </span>
              <span className="text-[10px] text-gray-500 -mt-1 hidden sm:block">{t('localFreshFast')}</span>
            </div>
          </Link>

          {/* Location (Desktop) */}
          {/* <div className="hidden md:flex items-center gap-2 text-sm cursor-pointer hover:text-orange-500 transition-colors" data-testid="location-selector">
            <MapPin size={18} className="text-orange-500" />
            <span className="text-gray-500">{t('deliverTo')}:</span>
            <span className="font-semibold text-gray-900">{t('yourLocation')}</span>
            <ChevronRight size={16} />
          </div> */}

          {/* Search (Desktop) */}
          {/* <div className="hidden lg:flex items-center flex-1 max-w-md mx-8">
            <div className="relative w-full">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder={t('searchPlaceholder')}
                className="w-full pl-12 pr-4 py-3 bg-gray-50 rounded-full border border-transparent focus:border-orange-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-orange-100 transition-all text-sm"
                data-testid="search-input"
              />
            </div>
          </div> */}

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-4">
            {/* Language Selector */}
            <LanguageSelector />
            
            {user ? (
              <>
                {/* User Info */}
                <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-full">
                  <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-orange-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                    {user.name?.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm font-semibold text-gray-900">{user.name}</span>
                    <span className="text-[10px] text-gray-500">{getRoleLabel()}</span>
                  </div>
                </div>

                <Link to={getDashboardLink()}>
                  <Button
                    data-testid="dashboard-btn"
                    className="bg-emerald-500 hover:bg-emerald-600 text-white h-11 px-6 rounded-full shadow-md hover:shadow-lg transition-all duration-300 active:scale-95 font-semibold"
                  >
                    {user.role === 'customer' && <Package className="w-5 h-5 mr-2" />}
                    {user.role === 'shop_owner' && <Package className="w-5 h-5 mr-2" />}
                    {user.role === 'delivery_agent' && <Truck className="w-5 h-5 mr-2" />}
                    {t('dashboard')}
                  </Button>
                </Link>
                <Button
                  data-testid="logout-btn"
                  onClick={handleLogout}
                  variant="ghost"
                  className="hover:bg-red-50 text-gray-600 hover:text-red-600 transition-colors h-11 px-4 rounded-full"
                >
                  <LogOut className="w-5 h-5 mr-2" />
                  {t('logout')}
                </Button>
              </>
            ) : (
              <Link to="/auth">
                <Button
                  data-testid="login-btn"
                  className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white h-11 px-8 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 active:scale-95 font-semibold"
                >
                  <User className="w-5 h-5 mr-2" />
                  {t('login')} / {t('register')}
                </Button>
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-100" data-testid="mobile-menu">
            <div className="space-y-4">
              {/* Mobile Search */}
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  placeholder={t('search') + '...'}
                  className="w-full pl-12 pr-4 py-3 bg-gray-50 rounded-full border border-transparent focus:border-orange-400 focus:bg-white focus:outline-none text-sm"
                />
              </div>

              {/* Mobile Location */}
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-xl">
                <MapPin size={18} className="text-orange-500" />
                <span className="text-sm text-gray-500">{t('deliverTo')}:</span>
                <span className="text-sm font-semibold text-gray-900">{t('yourLocation')}</span>
              </div>

              {/* Mobile Actions */}
              {user ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                    <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-orange-500 rounded-full flex items-center justify-center text-white font-bold">
                      {user.name?.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{user.name}</p>
                      <p className="text-xs text-gray-500">{getRoleLabel()}</p>
                    </div>
                  </div>
                  <Link to={getDashboardLink()} onClick={() => setMobileMenuOpen(false)}>
                    <Button className="w-full bg-emerald-500 hover:bg-emerald-600 text-white h-12 rounded-full font-semibold">
                      {t('goToDashboard')}
                    </Button>
                  </Link>
                  <Button
                    onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                    variant="outline"
                    className="w-full h-12 rounded-full text-red-600 border-red-200 hover:bg-red-50"
                  >
                    <LogOut className="w-5 h-5 mr-2" />
                    {t('logout')}
                  </Button>
                </div>
              ) : (
                <Link to="/auth" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 text-white h-12 rounded-full font-semibold">
                    <User className="w-5 h-5 mr-2" />
                    {t('login')} / {t('register')}
                  </Button>
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
